import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { History, Clock, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';

interface ExecutionSession {
  id: string;
  user_id: string;
  server_id: string;
  code_hash: string;
  status: 'success' | 'error' | 'timeout';
  output: string;
  error?: string;
  started_at: string;
  completed_at?: string;
  execution_time_ms: number;
  metrics?: {
    cpu_time_ms: number;
    memory_peak_mb: number;
    tool_call_count: number;
  };
  tools_invoked?: string[];
}

export function ExecutionHistory() {
  const [sessions, setSessions] = useState<ExecutionSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState<ExecutionSession | null>(null);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await api.get('/api/code-execution/sessions?limit=50');
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to load execution history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'timeout':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: 'default' | 'destructive' | 'secondary' } = {
      success: 'default',
      error: 'destructive',
      timeout: 'secondary',
    };
    return <Badge variant={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Sessions List */}
      <div className="lg:col-span-1">
        <Card className="p-4">
          <h3 className="text-lg font-semibold flex items-center mb-4">
            <History className="h-5 w-5 mr-2 text-blue-600" />
            Execution History
          </h3>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {sessions.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                No execution history yet. Run some code to see results here!
              </p>
            ) : (
              sessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => setSelectedSession(session)}
                  className={`p-3 border rounded-md cursor-pointer transition-colors ${
                    selectedSession?.id === session.id
                      ? 'bg-blue-50 border-blue-300'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(session.status)}
                      <span className="text-xs text-gray-500">
                        {new Date(session.started_at).toLocaleString()}
                      </span>
                    </div>
                    {getStatusBadge(session.status)}
                  </div>
                  
                  <div className="text-xs text-gray-600 flex items-center space-x-3">
                    <span>{session.execution_time_ms}ms</span>
                    {session.metrics && (
                      <>
                        <span>Tools: {session.metrics.tool_call_count}</span>
                        <span>Mem: {session.metrics.memory_peak_mb}MB</span>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Session Details */}
      <div className="lg:col-span-2">
        {selectedSession ? (
          <Card className="p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold">Session Details</h3>
                {getStatusBadge(selectedSession.status)}
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Session ID:</span>
                  <code className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded">
                    {selectedSession.id}
                  </code>
                </div>
                <div>
                  <span className="text-gray-600">Execution Time:</span>
                  <span className="ml-2 font-semibold">
                    {selectedSession.execution_time_ms}ms
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Started:</span>
                  <span className="ml-2">
                    {new Date(selectedSession.started_at).toLocaleString()}
                  </span>
                </div>
                {selectedSession.completed_at && (
                  <div>
                    <span className="text-gray-600">Completed:</span>
                    <span className="ml-2">
                      {new Date(selectedSession.completed_at).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {selectedSession.metrics && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold mb-3">Resource Metrics</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-blue-50 rounded-md">
                    <div className="text-xs text-gray-600">CPU Time</div>
                    <div className="text-lg font-semibold text-blue-700">
                      {selectedSession.metrics.cpu_time_ms}ms
                    </div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-md">
                    <div className="text-xs text-gray-600">Memory Peak</div>
                    <div className="text-lg font-semibold text-green-700">
                      {selectedSession.metrics.memory_peak_mb}MB
                    </div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-md">
                    <div className="text-xs text-gray-600">Tool Calls</div>
                    <div className="text-lg font-semibold text-purple-700">
                      {selectedSession.metrics.tool_call_count}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedSession.tools_invoked && selectedSession.tools_invoked.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold mb-2">Tools Invoked</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedSession.tools_invoked.map((tool, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="text-sm font-semibold mb-2">Output</h4>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-md overflow-x-auto text-sm max-h-96">
                {selectedSession.output || '(No output)'}
              </pre>
            </div>

            {selectedSession.error && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold mb-2 text-red-600">Error</h4>
                <pre className="bg-red-50 text-red-900 p-4 rounded-md overflow-x-auto text-sm">
                  {selectedSession.error}
                </pre>
              </div>
            )}
          </Card>
        ) : (
          <Card className="p-6 flex items-center justify-center h-full min-h-[400px]">
            <div className="text-center text-gray-500">
              <History className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p>Select a session to view details</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

